import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
    createProperty,
    getUserProperties,
    getPropertyById,
    updateProperty,
    deleteProperty,
} from '../../../firebase/property.service';
import { generatePropertyFormName, ensureFormFieldsHaveNames } from '../../../utils/formNameGenerator';
import useAppStore from '../../../store/appStore';

const DEFAULT_PROPERTY = {
    address: '',
    address2: '',
    price: 0,
    sqft: 0,
    bedrooms: 0,
    bathrooms: 0,
    description: '',
    pageUrl: '',
    imageUrls: [],
    thumbnailUrl: '',
    captureFormFields: [
        { name: 'First Name', type: 'text', required: false, locked: true },
        { name: 'Last Name', type: 'text', required: false, locked: true },
        { name: 'Email', type: 'email', required: true, locked: true },
    ],
    automations: {
        addToLeads: true,
        createLead: true,
        tags: [],
    },
    tags: [],
    stats: {
        totalViews: 0,
        totalLeads: 0,
        lastViewedAt: null,
    },
};

export function usePropertyData() {
    const { user } = useAuth();

    // ── Read properties from Zustand store (populated by realtimeSubscriptions) ──
    const storeProperties = useAppStore((s) => s.ctProperties);
    const loadingProperties = useAppStore((s) => s.loadingStates.properties);

    // Derive loading / properties from store
    const [properties, setProperties] = useState(storeProperties || []);
    const [loading, setLoading] = useState(loadingProperties || storeProperties === null);
    const [error, setError] = useState(null);

    // Single property state (when editing)
    const [currentPropertyId, setCurrentPropertyId] = useState(null);
    const [currentProperty, setCurrentProperty] = useState(DEFAULT_PROPERTY);

    // Dirty tracking — true once any field has been locally edited
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Track currentPropertyId in a ref to avoid re-subscribing to the listener
    const currentPropertyIdRef = useRef(currentPropertyId);
    useEffect(() => {
        currentPropertyIdRef.current = currentPropertyId;
    }, [currentPropertyId]);

    /**
     * Sync properties from Zustand store.
     * The realtime listener writes to Zustand, and this hook reads from it.
     */
    useEffect(() => {
        if (storeProperties === null) {
            setLoading(true);
            return;
        }

        setProperties(storeProperties);
        setLoading(false);

        // If we have a current property selected, update it from the new data
        const selectedPropertyId = currentPropertyIdRef.current;
        if (selectedPropertyId) {
            const updated = storeProperties.find(p => p.id === selectedPropertyId);
            if (updated) {
                setCurrentProperty(prev => ({
                    ...prev,
                    stats: updated.stats,
                    imageUrls: updated.imageUrls,
                    thumbnailUrl: updated.thumbnailUrl,
                }));
            }
        }
    }, [storeProperties]);

    /**
     * Create a new property
     * Note: Real-time listener will automatically add it to the list
     */
    const createNewProperty = useCallback(
        async (initialData = {}) => {
            if (!user?.uid) {
                setError('User not authenticated');
                return null;
            }

            try {
                setError(null);

                // Prepare property data with form names
                const propertyData = {
                    ...DEFAULT_PROPERTY,
                    ...initialData,
                };

                // Ensure captureFormFields have formName
                propertyData.captureFormFields = ensureFormFieldsHaveNames(
                    propertyData.captureFormFields,
                    propertyData.address
                );

                // Create property in Firestore
                const propertyId = await createProperty(user.uid, propertyData);

                // Set current property immediately for drawer (optimistic UI)
                const newProperty = {
                    id: propertyId,
                    userId: user.uid,
                    ...propertyData,
                };
                setCurrentPropertyId(propertyId);
                setCurrentProperty(newProperty);

                // Note: No need to manually add to properties list
                // The onSnapshot listener will detect the new doc and update automatically

                return propertyId;
            } catch (err) {
                console.error('Error creating property:', err);
                setError(err.message);
                return null;
            }
        },
        [user?.uid]
    );

    /**
     * Load specific property by ID
     * Ensures form fields have names (backward compatibility)
     */
    const loadProperty = useCallback(async (propertyId) => {
        try {
            setError(null);
            let property = await getPropertyById(propertyId);
            if (!property) {
                setError('Property not found');
                return null;
            }

            // Ensure form fields have names (backward compatibility)
            if (!property.captureFormFields?.[0]?.formName) {
                property.captureFormFields = ensureFormFieldsHaveNames(
                    property.captureFormFields,
                    property.address
                );
            }

            setCurrentPropertyId(propertyId);
            setCurrentProperty(property);
            return property;
        } catch (err) {
            console.error('Error loading property:', err);
            setError(err.message);
            return null;
        }
    }, []);

    /**
     * Update a single field — local state only, no backend write.
     * The user must click "Save Changes" to persist.
     * Special handling for 'address' field: also updates form name in captureFormFields.
     */
    const updateField = useCallback((field, value) => {
        if (!currentPropertyId) return;

        setCurrentProperty(prev => {
            const updated = {
                ...prev,
                [field]: value,
            };

            // When address changes, update formName in captureFormFields
            if (field === 'address' && Array.isArray(updated.captureFormFields)) {
                const newFormName = generatePropertyFormName(updated);
                updated.captureFormFields = updated.captureFormFields.map((f, idx) =>
                    idx === 0 ? { ...f, formName: newFormName } : f
                );
            }

            return updated;
        });

        setIsDirty(true);
    }, [currentPropertyId]);

    /**
     * Batch update multiple fields — local state only.
     */
    const updateFields = useCallback(
        (updates) => {
            if (!currentPropertyId) return;

            setCurrentProperty(prev => ({
                ...prev,
                ...updates,
            }));

            setIsDirty(true);
        },
        [currentPropertyId]
    );

    /**
     * Update nested object (e.g., automations, stats) — local state only.
     */
    const updateNestedField = useCallback(
        (parentField, childField, value) => {
            if (!currentPropertyId) return;

            setCurrentProperty(prev => ({
                ...prev,
                [parentField]: {
                    ...prev[parentField],
                    [childField]: value,
                },
            }));

            setIsDirty(true);
        },
        [currentPropertyId]
    );

    /**
     * Update array field (e.g., tags) — local state only.
     */
    const updateArrayField = useCallback(
        (arrayField, newArray) => {
            if (!currentPropertyId) return;

            setCurrentProperty(prev => ({
                ...prev,
                [arrayField]: newArray,
            }));

            setIsDirty(true);
        },
        [currentPropertyId]
    );

    /**
     * Immediately update image fields (called by upload hook after Firestore write)
     */
    const updateImageUrl = useCallback((field, url) => {
        setCurrentProperty(prev => ({
            ...prev,
            [field]: url,
        }));
    }, []);

    /**
     * Delete property
     * Note: Real-time listener will automatically remove from list
     */
    const deleteCurrentProperty = useCallback(async () => {
        if (!user?.uid || !currentPropertyId) {
            setError('Missing user or property ID');
            return false;
        }

        try {
            setError(null);
            await deleteProperty(user.uid, currentPropertyId);

            // Clear current (the onSnapshot listener will remove from list automatically)
            setCurrentPropertyId(null);
            setCurrentProperty(DEFAULT_PROPERTY);

            return true;
        } catch (err) {
            console.error('Error deleting property:', err);
            setError(err.message);
            return false;
        }
    }, [user?.uid, currentPropertyId]);

    /**
     * Save all local edits to Firestore in a single write.
     * Returns true on success, false on failure.
     */
    const saveAllFields = useCallback(async () => {
        if (!currentPropertyId) return false;

        setSaving(true);
        setError(null);
        try {
            const { id, userId, ...data } = currentProperty;
            await updateProperty(currentPropertyId, data);
            setIsDirty(false);
            return true;
        } catch (err) {
            console.error('Error saving property:', err);
            setError(err.message);
            return false;
        } finally {
            setSaving(false);
        }
    }, [currentPropertyId, currentProperty]);

    /**
     * Clear current property
     */
    const clearCurrent = useCallback(() => {
        setCurrentPropertyId(null);
        setCurrentProperty(DEFAULT_PROPERTY);
        setIsDirty(false);
        setError(null);
    }, []);

    return {
        // List state
        properties,
        loading,
        error,

        // Current property state
        currentPropertyId,
        currentProperty,
        isDirty,
        saving,

        // List operations
        createNewProperty,
        loadProperty,
        deleteCurrentProperty,
        clearCurrent,

        // Update operations (local-only until saveAllFields is called)
        updateField,
        updateFields,
        updateNestedField,
        updateArrayField,
        updateImageUrl, // Called by image upload hook

        // Persist
        saveAllFields,
    };
}
